import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminStore } from '../../store/adminStore';
import { adminApi } from '../../lib/api';
import { Lock, Mail, LogIn, AlertTriangle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { CaptchaField } from '../../components/CaptchaField';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const login = useAdminStore(state => state.login);
  const captchaRef = useRef(null);

  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLockoutMessage('');

    try {
      const response = await adminApi.login({
        ...credentials,
        captcha_token: captchaToken || ''
      });
      login(response.data.token, response.data.email);
      toast.success('Bienvenido!');
      navigate('/admin/dashboard');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || '';

      if (status === 429) {
        // Rate limited / locked out
        setLockoutMessage(detail);
        toast.error('Cuenta bloqueada temporalmente');
      } else if (status === 400) {
        toast.error(detail || 'Captcha inválido');
      } else if (status === 401) {
        toast.error(detail || 'Credenciales inválidas');
      } else {
        toast.error('Error de conexión. Intente nuevamente.');
      }
      // Reset captcha on any error
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const hasSitekey = !!process.env.REACT_APP_HCAPTCHA_SITEKEY;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-manrope font-bold text-2xl text-emerald-950 mb-2">
              Panel de Administración
            </h1>
            <p className="text-slate-600">Sam's Transfer</p>
          </div>

          {lockoutMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Acceso bloqueado</p>
                <p className="text-sm text-red-600 mt-1">{lockoutMessage}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-600" />
                Email
              </label>
              <input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                placeholder="admin@ejemplo.com"
                className="w-full h-12 px-4 bg-white border border-slate-200 rounded-lg
                  focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200
                  transition-all outline-none"
                data-testid="admin-email-input"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                <Lock className="w-4 h-4 text-emerald-600" />
                Contraseña
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                placeholder="••••••••"
                className="w-full h-12 px-4 bg-white border border-slate-200 rounded-lg
                  focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200
                  transition-all outline-none"
                data-testid="admin-password-input"
                required
                autoComplete="current-password"
              />
            </div>

            <CaptchaField
              ref={captchaRef}
              onVerify={setCaptchaToken}
            />

            <button
              type="submit"
              disabled={loading || !!lockoutMessage || (hasSitekey && !captchaToken)}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white
                font-semibold rounded-lg shadow-lg transition-all
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Iniciar Sesión</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-100">
            <p className="text-xs text-center text-slate-400">
              Acceso protegido con bloqueo por intentos fallidos.
              <br />
              Si olvidó su contraseña, contacte al administrador del sistema.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
